/* AppHub — Netflix / Prime-style entry screen shown at app launch.
   ────────────────────────────────────────────────────────────────
   Route: /hub  (start_url in manifest.json)

   Behavior:
   - Rotating full-bleed hero (6s per slide, cross-fade) with ORIO-brand
     photorealistic-illustrative art on soft pastel backgrounds. Each
     slide teases one feature so users discover ORIO before entering.
   - AUTHENTICATED users: show the user's avatar big + name + "Entra",
     a locked "Family" placeholder tile (future paid plan), and a
     "Modifica profilo" secondary action. Tapping the avatar navigates
     to /app.
   - GUEST users: show "Accedi con Google" + "Accedi con email" +
     "Iscriviti" — all wired to the existing Login/Register flows.
   - No bottom navbar (fullscreen immersive).
*/
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { LogIn, UserPlus, Lock, Pencil, LogOut, Users, Building2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { hapticTap } from "@/lib/haptics";

/* ─── Hero slides. Each slide has: gradient background (ORIO warm/pastel),
   accent color, feature name, and short teaser. When we later have real
   Nano-Banana photorealistic art, drop the image URL in `image` and the
   gradient will hide behind it. ─────────────────────────────────────── */
const SLIDES = [
  {
    key: "sos",
    tag: "SOS · IL CUORE DI ORIO",
    title: "Se il tuo cane scappa, in un tap allerti 80 km",
    accent: "#FF7A1A",
    image: "/hub/hero-sos.png",
  },
  {
    key: "segnalazioni",
    tag: "SEGNALAZIONI",
    title: "Ogni pericolo condiviso protegge tutti i cani",
    accent: "#E56F3A",
    image: "/hub/hero-segnalazioni.png",
  },
  {
    key: "stories-match",
    tag: "STORIES & MATCH",
    title: "Compagni di passeggiata a portata di zampa",
    accent: "#C67B76",
    image: "/hub/hero-stories.png",
  },
];

export default function AppHub() {
  const { user, loading, logout: authLogout } = useAuth();
  const navigate = useNavigate();
  const [slideIdx, setSlideIdx] = useState(0);

  // Auto-rotate the hero every 6s (pause not needed — light animation).
  useEffect(() => {
    const t = setInterval(() => setSlideIdx((i) => (i + 1) % SLIDES.length), 6000);
    return () => clearInterval(t);
  }, []);

  const slide = SLIDES[slideIdx];

  const enter = () => {
    hapticTap();
    try {
      // Bump the idle heartbeat so Home doesn't bounce us back to /hub.
      localStorage.setItem("orio_last_active", String(Date.now()));
      sessionStorage.setItem("orio_hub_seen", "1");
    } catch { /* storage disabled — proceed */ }
    navigate("/app");
  };

  const openShelter = () => {
    hapticTap();
    if (!user?.is_shelter) {
      toast.error("Solo gli account Rifugio possono accedere. Iscriviti come Rifugio dal profilo per attivarlo.");
      return;
    }
    try {
      localStorage.setItem("orio_last_active", String(Date.now()));
      sessionStorage.setItem("orio_hub_seen", "1");
    } catch { /* proceed */ }
    navigate("/app/rifugio");
  };

  const logout = async () => {
    hapticTap();
    try {
      // AuthContext.logout() owns clearing the httpOnly cookie AND the
      // orio_token Bearer fallback in localStorage — using it here means
      // the UI cannot end up in the "logged out but still showing profile
      // chooser" limbo state that iteration_45 caught.
      await authLogout();
    } catch { /* proceed anyway */ }
    try {
      sessionStorage.removeItem("orio_hub_seen");
      localStorage.removeItem("orio_last_active");
    } catch { /* proceed */ }
    navigate("/hub", { replace: true });
  };

  return (
    <div
      className="fixed inset-0 overflow-hidden text-white select-none flex flex-col"
      style={{ background: "#FBF7F1" }}
      data-testid="app-hub"
    >
      {/* HERO — top ~58% of screen. The image stays TALL and framed so the
         subjects (faces + dogs) are always in the visible half, then a strong
         ORIO-orange gradient rises from the bottom to blend the image into the
         action panel below without ever covering the subject. */}
      <div className="relative w-full" style={{ height: "58vh", minHeight: 380 }}>
        <AnimatePresence mode="sync">
          <motion.div
            key={slide.key}
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.02 }}
            transition={{ duration: 1.2, ease: "easeInOut" }}
            className="absolute inset-0"
          >
            <img
              src={slide.image}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full object-cover"
              style={{ objectPosition: "center 65%" }}
              draggable={false}
            />
          </motion.div>
        </AnimatePresence>

        {/* ORIO-ORANGE bottom gradient — starts higher up (~65%) so the subject
           (face + dog) is preserved but the caption + CTAs below sit on solid
           warm orange, continuously blending into the action panel. */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(to bottom, rgba(255,122,26,0) 0%, rgba(255,122,26,0) 62%, rgba(255,138,50,0.55) 78%, rgba(255,122,26,0.95) 92%, #FF7A1A 100%)",
          }}
        />
        {/* Top translucent hairline for status-bar contrast against light skies */}
        <div
          className="absolute inset-x-0 top-0 h-24 pointer-events-none"
          style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.28), transparent)" }}
        />

        {/* ORIO logo — white, top-CENTER, above the image */}
        <div
          className="absolute top-[calc(env(safe-area-inset-top,0px)+16px)] left-0 right-0 flex justify-center z-10"
        >
          <img
            src="/brand/orio-wordmark.svg"
            alt="ORIO"
            className="h-7 opacity-95"
            style={{ filter: "brightness(0) invert(1) drop-shadow(0 2px 10px rgba(0,0,0,0.35))" }}
            draggable={false}
          />
        </div>

        {/* Feature caption — TITLE ONLY (no subtitle), sits inside the orange
           gradient at the bottom of the hero. */}
        <div className="absolute left-0 right-0 z-10 bottom-[54px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={slide.key + "-caption"}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.55, ease: "easeOut" }}
              className="max-w-md mx-auto px-6 text-center"
            >
              <div
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.22em] font-bold"
                style={{
                  background: "rgba(255,255,255,0.18)",
                  color: "#FFFFFF",
                  border: "1px solid rgba(255,255,255,0.55)",
                  backdropFilter: "blur(10px)",
                }}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-white" />
                {slide.tag}
              </div>
              <h1
                className="font-display text-[22px] sm:text-[26px] leading-tight mt-3 text-white"
                style={{ textShadow: "0 2px 12px rgba(120,45,0,0.55), 0 1px 3px rgba(80,30,0,0.55)" }}
              >
                {slide.title}
              </h1>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Slide dots — anchored inside the hero, on the orange band */}
        <div className="absolute inset-x-0 bottom-[26px] z-10 flex items-center justify-center gap-1.5">
          {SLIDES.map((s, i) => (
            <button
              key={s.key}
              onClick={() => setSlideIdx(i)}
              aria-label={`Vai a ${s.tag}`}
              data-testid={`hub-dot-${i}`}
              className="h-1.5 rounded-full transition-all"
              style={{
                width: i === slideIdx ? 22 : 6,
                background: i === slideIdx ? "#FFFFFF" : "rgba(255,255,255,0.55)",
                boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
              }}
            />
          ))}
        </div>
      </div>

      {/* PROFILE / AUTH PANEL — warm orange background continues from the hero
         gradient so the CTAs live on the orange band, exactly the "Netflix
         with ORIO warmth" look the user asked for. */}
      <div
        className="flex-1 flex flex-col px-6"
        style={{
          background: "linear-gradient(to bottom, #FF7A1A 0%, #F4901E 55%, #E56F1A 100%)",
          color: "#FFFFFF",
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 22px)",
          paddingTop: 18,
        }}
      >
        {loading ? (
          <div className="text-center pt-10 text-white/80 text-sm">Sto caricando…</div>
        ) : user ? (
          <ProfileChooser
            user={user}
            onEnter={enter}
            onEdit={() => navigate("/app/profilo")}
            onLogout={logout}
            onShelter={openShelter}
          />
        ) : (
          <GuestActions />
        )}
      </div>
    </div>
  );
}
/* ─── AUTH'd: 3-tile chooser (Me · Family · Rifugio) + Modifica + Logout ── */
function ProfileChooser({ user, onEnter, onEdit, onLogout, onShelter }) {
  const initial = (user?.name || "U").trim().charAt(0).toUpperCase();
  const [avatarBroken, setAvatarBroken] = useState(false);
  const isShelter = !!user?.is_shelter;
  return (
    <div className="max-w-md mx-auto w-full">
      <div className="text-center text-white/85 text-[11px] uppercase tracking-widest font-semibold mb-4">
        Chi sta usando ORIO?
      </div>

      {/* 3-tile grid — same rounded-square, same size, always aligned */}
      <div className="grid grid-cols-3 gap-3">
        {/* MINE — tap enters the app */}
        <button
          onClick={onEnter}
          data-testid="hub-profile-me"
          className="group flex flex-col items-center gap-2 active:scale-95 transition-transform"
        >
          <div
            className="h-[84px] w-[84px] rounded-[22px] overflow-hidden flex items-center justify-center border-2 border-white/95 bg-white"
            style={{ boxShadow: "0 14px 30px -12px rgba(0,0,0,0.45)" }}
          >
            {user.photo_url && !avatarBroken ? (
              <img
                src={user.photo_url}
                alt=""
                referrerPolicy="no-referrer"
                onError={() => setAvatarBroken(true)}
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="font-display text-3xl font-semibold" style={{ color: "#FF7A1A" }}>
                {initial}
              </span>
            )}
          </div>
          <div className="text-[12.5px] font-semibold text-white leading-tight max-w-[92px] truncate">
            {user.name?.split(" ")[0] || user.email?.split("@")[0] || "Io"}
          </div>
        </button>

        {/* FAMILY — locked (future paid tier) */}
        <button
          onClick={() => {
            hapticTap();
            toast("Family arriverà con il piano multi-utente. Ti avviseremo.", { icon: "👨‍👩‍👧" });
          }}
          data-testid="hub-profile-family"
          className="group flex flex-col items-center gap-2 active:scale-95 transition-transform"
        >
          <div
            className="relative h-[84px] w-[84px] rounded-[22px] overflow-hidden flex items-center justify-center"
            style={{
              background: "rgba(255,255,255,0.14)",
              border: "2px solid rgba(255,255,255,0.35)",
              backdropFilter: "blur(8px)",
              boxShadow: "0 14px 30px -12px rgba(0,0,0,0.35)",
            }}
          >
            <Users className="h-9 w-9 text-white/90" />
            <div className="absolute bottom-1.5 right-1.5 h-6 w-6 rounded-full bg-white/25 backdrop-blur-md border border-white/50 flex items-center justify-center">
              <Lock className="h-3 w-3 text-white" />
            </div>
          </div>
          <div className="text-[12.5px] font-semibold text-white leading-tight">Family</div>
        </button>

        {/* SHELTER — only if user registered as shelter */}
        <button
          onClick={onShelter}
          data-testid="hub-profile-shelter"
          className="group flex flex-col items-center gap-2 active:scale-95 transition-transform"
        >
          <div
            className="relative h-[84px] w-[84px] rounded-[22px] overflow-hidden flex items-center justify-center"
            style={{
              background: isShelter
                ? "linear-gradient(135deg, #5B9EE0 0%, #7B61FF 100%)"
                : "rgba(255,255,255,0.14)",
              border: isShelter ? "2px solid rgba(255,255,255,0.65)" : "2px solid rgba(255,255,255,0.35)",
              backdropFilter: "blur(8px)",
              boxShadow: isShelter
                ? "0 14px 30px -12px rgba(91,158,224,0.55)"
                : "0 14px 30px -12px rgba(0,0,0,0.35)",
            }}
          >
            <Building2 className={`h-9 w-9 ${isShelter ? "text-white" : "text-white/85"}`} />
            {!isShelter && (
              <div className="absolute bottom-1.5 right-1.5 h-6 w-6 rounded-full bg-white/25 backdrop-blur-md border border-white/50 flex items-center justify-center">
                <Lock className="h-3 w-3 text-white" />
              </div>
            )}
          </div>
          <div className="text-[12.5px] font-semibold text-white leading-tight">Rifugio</div>
        </button>
      </div>

      {/* Actions row — Modifica + Logout */}
      <div className="mt-6 grid grid-cols-2 gap-3">
        <button
          onClick={onEdit}
          data-testid="hub-edit"
          className="h-11 rounded-2xl bg-white/12 border border-white/40 text-white text-[13px] font-semibold inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform backdrop-blur"
        >
          <Pencil className="h-3.5 w-3.5" /> Modifica profilo
        </button>
        <button
          onClick={onLogout}
          data-testid="hub-logout"
          className="h-11 rounded-2xl bg-white/12 border border-white/40 text-white text-[13px] font-semibold inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform backdrop-blur"
        >
          <LogOut className="h-3.5 w-3.5" /> Esci
        </button>
      </div>
    </div>
  );
}

/* ─── GUEST: auth CTAs ─────────────────────────────────────────── */
function GuestActions() {
  const goGoogle = () => {
    hapticTap();
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
    // URLS, THIS BREAKS THE AUTH.
    const redirectUrl = window.location.origin + "/app";
    window.location.href =
      "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
  };

  return (
    <div className="max-w-md mx-auto w-full space-y-3">
      <button
        onClick={goGoogle}
        data-testid="hub-google"
        className="w-full h-12 rounded-2xl bg-white text-[#1A1A1A] text-[14px] font-semibold inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
        style={{ boxShadow: "0 12px 26px -10px rgba(0,0,0,0.4)" }}
      >
        <GoogleGlyph /> Continua con Google
      </button>
      <div className="grid grid-cols-2 gap-3">
        <Link
          to="/login"
          data-testid="hub-login"
          className="h-11 rounded-2xl bg-white/14 border border-white/45 text-white text-[13px] font-semibold inline-flex items-center justify-center gap-2 backdrop-blur"
        >
          <LogIn className="h-3.5 w-3.5" /> Accedi
        </Link>
        <Link
          to="/register"
          data-testid="hub-register"
          className="h-11 rounded-2xl text-[#C25612] text-[13px] font-bold inline-flex items-center justify-center gap-2 bg-white"
          style={{ boxShadow: "0 10px 22px -8px rgba(0,0,0,0.35)" }}
        >
          <UserPlus className="h-3.5 w-3.5" /> Iscriviti
        </Link>
      </div>
      <div className="text-[11px] text-white/85 text-center pt-2">
        30 giorni di ORIO Pro gratis · Disdici quando vuoi.
      </div>
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
      <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.7 4.1-5.5 4.1-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.15.8 3.87 1.5l2.64-2.55C16.9 3.35 14.65 2.4 12 2.4 6.75 2.4 2.5 6.65 2.5 12s4.25 9.6 9.5 9.6c5.5 0 9.15-3.85 9.15-9.3 0-.62-.07-1.1-.15-1.6H12z"/>
      <path fill="#FBBC05" d="M12 21.6c2.55 0 4.7-.85 6.27-2.3l-3-2.35c-.85.6-2 .95-3.27.95-2.55 0-4.7-1.7-5.47-4.05l-3.1 2.4C4.8 19.35 8.15 21.6 12 21.6z"/>
      <path fill="#4285F4" d="M21.15 12c0-.62-.07-1.1-.15-1.6H12v3.9h5.5c-.24 1.05-.95 2.05-2 2.75l3 2.35c1.75-1.6 2.65-4 2.65-7.4z"/>
    </svg>
  );
}
